import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './NLPSentiment.css';
import GaugeChart from 'react-gauge-chart';

function NLPSentiment() {
  const navigate = useNavigate();
  const [review, setReview] = useState('');
  const [result, setResult] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Sample reviews
  const sampleReviews = {
    positive: "This film absolutely blew me away from start to finish. The cinematography was breathtaking, and every frame felt like a work of art. The lead actor delivered a performance that was both powerful and nuanced, bringing real depth to what could have been a one-dimensional character. The script was tight, the pacing was perfect, and the emotional beats hit exactly when they needed to. I found myself completely immersed in the story, and the ending left me speechless. This is the kind of movie that reminds you why you fell in love with cinema in the first place. Highly recommend seeing it on the biggest screen possible!",

    mixed: "I had mixed feelings about this one. The special effects were impressive and the action sequences were well choreographed, but the plot felt rushed and underdeveloped. Some of the dialogue was genuinely clever and funny, while other scenes dragged on too long without adding much to the story. The lead actress gave a solid performance, but the supporting cast seemed to be phoning it in. The first half kept my attention, but the second half lost momentum. It's not a bad way to spend two hours if you're looking for mindless entertainment, but don't expect anything groundbreaking. Worth a watch if you're a fan of the genre, but probably not worth seeing twice.",

    negative: "What a complete waste of time and money. The plot made absolutely no sense and felt like it was written by someone who had never seen a movie before. The acting was wooden and unconvincing across the board - I've seen better performances in high school plays. The pacing was all over the place, with scenes that either moved too fast or dragged on forever. The special effects looked cheap and dated, even for a low-budget film. I kept waiting for something, anything, to redeem this disaster, but it just got worse as it went on. The ending was predictable and unsatisfying. Save yourself the disappointment and skip this one entirely. I can't believe I sat through the whole thing."
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('https://imdb-sentiment-backend.onrender.com/api/nlp/predict', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review }),
      });
      const data = await res.json();
      setResult(data);
    } catch (error) {
      setResult({ error: 'Server error or not running.' });
    }
  };

  const loadSampleReview = (type) => {
    setReview(sampleReviews[type]);
    setResult(null); // Clear previous results
  };

  return (
    <>
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2>How This AI Works</h2>

            <div className="explanation-section">
              <h3>Technical Explanation</h3>
              <p>
                <strong>Step 1:</strong> Load the IMDB dataset containing 50,000 movie reviews (25,000 positive, 25,000 negative).
                <strong> Step 2:</strong> Preprocess the text by removing HTML tags, converting to lowercase, and tokenizing.
                <strong> Step 3:</strong> Transform the text into numerical features using TF-IDF vectorization, which calculates how important each word is to each review relative to the entire dataset.
                <strong> Step 4:</strong> Train a Linear Support Vector Classifier (SVC) on the TF-IDF features to learn patterns that distinguish positive from negative sentiment.
                <strong> Step 5:</strong> Evaluate the model on test data.
                <strong> Step 6:</strong> Deploy the trained model via Flask API. When you submit a review, it's vectorized using the same TF-IDF transformer and classified by the trained SVC.
              </p>
            </div>

            <div className="explanation-section simply-put">
              <h3>Simply Put</h3>
              <p>
                The AI learned to recognize positive and negative movie reviews by reading 50,000 examples. It figured out which words appear more in good reviews (like 'amazing,' 'excellent') versus bad reviews (like 'terrible,' 'boring'). Now when you write a review, it checks which type of words you used more and tells you if your review sounds positive or negative.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="App">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back to Portfolio
        </button>
        <button className="info-btn" onClick={() => setShowModal(true)}>
          ℹ️ How It Works
        </button>
        <h1>Movie Review Sentiment Analyzer</h1>

      <div className="samples-section">
        <p className="samples-label">Sample Reviews:</p>
        <div className="sample-buttons">
          <button
            type="button"
            className="sample-btn positive-btn"
            onClick={() => loadSampleReview('positive')}
          >
            Positive
          </button>
          <button
            type="button"
            className="sample-btn mixed-btn"
            onClick={() => loadSampleReview('mixed')}
          >
            Mixed
          </button>
          <button
            type="button"
            className="sample-btn negative-btn"
            onClick={() => loadSampleReview('negative')}
          >
            Negative
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <textarea
          rows="6"
          placeholder="Enter a movie review..."
          value={review}
          onChange={(e) => setReview(e.target.value)}
          required
        />
        <br />
        <button type="submit">Analyze</button>
      </form>

      {result && (
        <div className="result">
          {result.error ? (
            <p className="error">{result.error}</p>
          ) : (
            <>
              <p><strong>Sentiment:</strong> {result.sentiment}</p>
              <p><strong>Score:</strong> {result.score.toFixed(4)}</p>
              <p><strong>Gauge:</strong> {result.gauge}</p>
              <GaugeChart
                id="sentiment-gauge"
                nrOfLevels={20}
                percent={result.score}
                textColor="#000000"
                colors={["#FF0000", "#FFFF00", "#00FF00"]}
              />
            </>
          )}
        </div>
      )}

      <hr style={{ margin: '40px 0' }} />

      <div className="walkthrough" style={{ textAlign: 'left' }}>
        <h2>How This Model Was Built</h2>
        <ul>
          <li><strong>Dataset:</strong> IMDB movie reviews with 50/50 positive/negative balance.</li>
          <li><strong>Cleaning:</strong> Removed HTML tags, punctuation, stopwords; converted to lowercase.</li>
          <li><strong>Vectorization:</strong> TF-IDF with unigrams and bigrams.</li>
          <li><strong>Model:</strong> Trained using Logistic Regression and improved with Linear SVC.</li>
          <li><strong>Evaluation:</strong> Achieved 90% accuracy with confusion matrix and evaluation metrics.</li>
          <li><strong>Deployment:</strong> Integrated into this React app with real-time predictions.</li>
        </ul>
        <p style={{ marginTop: '1em' }}>
          See the full notebook for preprocessing steps, code, and model evaluation.
        </p>
      </div>
    </div>
    </>
  );
}

export default NLPSentiment;
